import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScrollText, Plus, Crown, Users, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, Modal, Field, TextInput, Btn } from "@/components/common";

export default function Campaigns() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", setting: "" });
  const [code, setCode] = useState("");

  const { data: camps, isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => (await api.get("/campaigns")).data,
  });

  const create = useMutation({
    mutationFn: async (p) => (await api.post("/campaigns", p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns"] }); setOpen(false); setForm({ name: "", description: "", setting: "" }); toast.success("Campaign created!"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const join = useMutation({
    mutationFn: async (invite_code) => (await api.post("/campaigns/join", { invite_code })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns"] }); setJoinOpen(false); setCode(""); toast.success("Joined campaign!"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader
        title="Campaigns"
        subtitle="Your adventures and parties"
        icon={ScrollText}
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" data-testid="join-campaign-btn" onClick={() => setJoinOpen(true)}><KeyRound size={16} /> Join</Btn>
            <Btn data-testid="new-campaign-btn" onClick={() => setOpen(true)}><Plus size={16} /> New</Btn>
          </div>
        }
      />

      {camps?.length ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {camps.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Link to={`/campaigns/${c.id}`} data-testid={`campaign-card-${c.id}`} className="glass glass-hover rounded-3xl p-5 block h-full">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-700 flex items-center justify-center">
                    <ScrollText size={22} className="text-white" />
                  </div>
                  {c.is_dm && <span className="flex items-center gap-1 text-xs text-amber-300 bg-amber-500/15 px-2.5 py-1 rounded-full"><Crown size={12} /> DM</span>}
                </div>
                <div className="font-display text-lg font-semibold text-white">{c.name}</div>
                {c.setting && <div className="text-xs text-violet-300/70 mt-0.5">{c.setting}</div>}
                <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{c.description || "No description"}</p>
                <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-4">
                  <Users size={14} /> {c.member_count} member{c.member_count !== 1 ? "s" : ""}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={ScrollText}
          title="No campaigns yet"
          subtitle="Create a campaign as DM or join one with an invite code."
          action={<Btn onClick={() => setOpen(true)}><Plus size={16} /> Create Campaign</Btn>}
        />
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Create Campaign">
        <div className="space-y-4">
          <Field label="Name"><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name" placeholder="The Lost Mines" /></Field>
          <Field label="Setting"><TextInput value={form.setting} onChange={(e) => setForm({ ...form, setting: e.target.value })} placeholder="Forgotten Realms" /></Field>
          <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm min-h-[90px]" /></Field>
          <Btn className="w-full" data-testid="confirm-create-campaign" onClick={() => form.name.trim() ? create.mutate(form) : toast.error("Name required")}>Create as DM</Btn>
        </div>
      </Modal>

      <Modal open={joinOpen} onClose={() => setJoinOpen(false)} title="Join a Campaign" size="sm">
        <div className="space-y-4">
          <Field label="Invite Code"><TextInput value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="join-code-input" placeholder="ABC123" className="font-mono tracking-widest text-center text-lg" /></Field>
          <Btn className="w-full" data-testid="confirm-join" onClick={() => code.trim() ? join.mutate(code) : toast.error("Enter a code")}>Join</Btn>
        </div>
      </Modal>
    </div>
  );
}
